import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cmtIssueActions from "../../../_actions/_qmsActions/cmtIssueActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./cmtIssue.css";
import qmsUserActions from "../../../_actions/_qmsActions/qmsUserActions";
import qmsDocumentActions from "../../../_actions/_qmsActions/qmsDocumentActions";
import cmtStageActions from "../../../_actions/_qmsActions/cmtStageActions";
import cmtCategoryActions from "../../../_actions/_qmsActions/cmtCategoryActions";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";
import { getQmsDocumentLabel } from "../_common/qmsUtilities";
import qmsDocumentTypeActions from "../../../_actions/_qmsActions/qmsDocumentTypeActions";
import { QMS } from "../../../_constants/actionConstants";
import CmtModal_EditLinkedDocuments from "./CmtModal_EditLinkedDocuments";

export default function CmtIssueHeaderDetails(props) {
  const { issueId, divisionId, editIsAuthorized_qmr } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(cmtCategoryActions.getAll(divisionId));
    dispatch(cmtStageActions.getAll(divisionId));
    dispatch(cmtIssueActions.get(issueId));
    dispatch(qmsUserActions.getAllForIssue(issueId));
    dispatch(qmsTeamActions.getAll(divisionId));
    dispatch(qmsDocumentTypeActions.getAll(divisionId));
    dispatch(qmsDocumentActions.getAll(divisionId));
  }, []);

  const issue = useSelector((state) => state.qms.openIssues[issueId] || null);
  const qmsFrameworkChanges = useSelector(
    (state) => state.qms.qmsFrameworkChanges[divisionId] || null
  );
  const cmtCategories = useSelector(
    (state) => state.qms.cmtCategories[divisionId] || null
  );
  const qmsDocumentTypes = useSelector(
    (state) => state.qms.qmsDocumentTypes[divisionId] || null
  );
  const qmsDocuments = useSelector((state) => state.qms.qmsDocuments[divisionId] || null);

  const [fieldsInEditMode, setFieldsInEditMode] = useState({
    subject: false,
    description: false,
    qmsFrameworkChangeId: false,
    categoryIds: false,
    linkedDocuments: false,
  });
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [qmsFrameworkChangeId, setQmsFrameworkChangeId] = useState(0);
  const [inputCategory, setInputCategory] = useState(0);
  const [modalState_editLinkedDocuments, setModalState_editLinkedDocuments] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_editLinkedDocuments = () =>
    setModalState_editLinkedDocuments({
      ...modalState_editLinkedDocuments,
      showModal: !modalState_editLinkedDocuments.showModal,
    });

  const fetching = useSelector((state) => state.qms.fetching);
  const getSubject = useCallback(
    () => (issue ? issue.subject : fetching ? "Loading..." : "(N/A)"),
    [issue, fetching]
  );
  const getDescription = useCallback(
    () => (issue ? issue.description || "(N/A)" : fetching ? "Loading..." : "(N/A)"),
    [issue, fetching]
  );
  const getQmsFrameworkChange = useCallback(() => {
    if (issue) {
      const qfc = (qmsFrameworkChanges || []).find(
        (c) => c.qmsFrameworkChangeId === issue.qmsFrameworkChangeId
      );
      return qfc ? qfc.label : "(N/A)";
    }
    if (fetching) return "Loading...";
    return "(N/A)";
  }, [qmsFrameworkChanges, fetching]);

  const getInputCategories = useCallback(() => {
    const categoryId = issue && (issue.categoryIds || [])[0];
    if (issue && categoryId) {
      const cmtCategory = (cmtCategories || []).find(
        (cc) => cc.cmtCategoryId === categoryId
      );
      if (cmtCategory)
        return cmtCategory ? (
          <>
            <span className="mr-1">{cmtCategory.label}:</span>
            <span>{cmtCategory.description}</span>
          </>
        ) : (
          "(N/A)"
        );
    }
    return "(N/A)";
  }, [issue, cmtCategories]);

  const getLinkedDocuments = useCallback(() => {
    if (!issue) return [];
    const docs = (qmsDocuments || []).filter((qd) =>
      (issue.linkedDocuments || []).some((ild) => ild.qmsDocumentId === qd.qmsDocumentId)
    );
    const docLabels = docs.map((qd) =>
      qmsDocumentTypes
        ? getQmsDocumentLabel(qd, qmsDocumentTypes)
        : fetching
        ? "Loading..."
        : "(N/A)"
    );
    return docLabels.length > 0 ? docLabels.join("; ") : "(N/A)";
  }, [issue, qmsDocuments, qmsDocumentTypes, fetching]);

  const handleClick_saveHeader = (fieldName, value) => async (e) => {
    e.preventDefault();
    if (!issue) return;

    dispatch({
      type: QMS.ISSUE.UPDATE_CLIENT,
      issueId: issue.issueId,
      fieldsToUpdate: { [fieldName]: value },
    });
    await dispatch(cmtIssueActions.update(issue.issueId));
    dispatch(cmtIssueActions.getAll(divisionId));
    setFieldsInEditMode({ ...fieldsInEditMode, [fieldName]: false });
  };

  const handleClick_revertHeader = (fieldName) => (e) => {
    e.preventDefault();
    setFieldsInEditMode({ ...fieldsInEditMode, [fieldName]: false });
  };

  const handleClick_downloadFileFromInitiator = (iaId, filename) => (e) => {
    e.preventDefault();
    dispatch(cmtIssueActions.downloadFile(iaId, filename));
  };

  const showEdit =
    issue && issue.isAcknowledged && !issue.isClosed && editIsAuthorized_qmr;

  return (
    <>
      <div className="card flex-grow-1">
        <div className="card-body p-2">
          <ul className="list-group-flush pl-0 mb-0">
            <li className="list-group-item border-bottom py-0 pl-1">
              <span className="mr-2 font-weight-bold">Subject:</span>
              {showEdit && !fieldsInEditMode.subject && (
                <CmtHeaderEditButton
                  buttonId="editSubject"
                  handleClick={() => {
                    setSubject(issue ? issue.subject : "");
                    setFieldsInEditMode({ ...fieldsInEditMode, subject: true });
                  }}
                />
              )}
              <br />
              {fieldsInEditMode.subject ? (
                <CmtHeaderEdit
                  saveId="saveSubject"
                  revertId="revertSubject"
                  handleClick_save={handleClick_saveHeader("subject", subject)}
                  handleClick_revert={handleClick_revertHeader("subject")}
                >
                  <input
                    id="editSubject"
                    name="editSubject"
                    className="form-control form-control-sm"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </CmtHeaderEdit>
              ) : (
                <span className="issue-subject mr-2">{getSubject()}</span>
              )}
            </li>
            <li className="list-group-item border-bottom py-0 pl-1">
              <span className="font-weight-bold mr-2">Description:</span>
              {showEdit && !fieldsInEditMode.description && (
                <CmtHeaderEditButton
                  buttonId="editDescription"
                  handleClick={() => {
                    setDescription(issue ? issue.description : "");
                    setFieldsInEditMode({ ...fieldsInEditMode, description: true });
                  }}
                />
              )}
              <br />
              {fieldsInEditMode.description ? (
                <CmtHeaderEdit
                  saveId="saveDescription"
                  revertId="revertDescription"
                  handleClick_save={handleClick_saveHeader("description", description)}
                  handleClick_revert={handleClick_revertHeader("description")}
                >
                  <input
                    id="editDescription"
                    name="editDescription"
                    className="form-control form-control-sm"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </CmtHeaderEdit>
              ) : (
                <span className="issue-description">{getDescription()}</span>
              )}
            </li>
            <li className="list-group-item border-bottom py-0 pl-1">
              <span className="mr-2 font-weight-bold">QMS Framework Change:</span>
              {showEdit && !fieldsInEditMode.qmsFrameworkChangeId && (
                <CmtHeaderEditButton
                  buttonId="editQmsFrameworkChange"
                  handleClick={() => {
                    setQmsFrameworkChangeId(issue ? issue.qmsFrameworkChangeId : 0);
                    setFieldsInEditMode({
                      ...fieldsInEditMode,
                      qmsFrameworkChangeId: true,
                    });
                  }}
                />
              )}
              <br />
              {fieldsInEditMode.qmsFrameworkChangeId ? (
                <CmtHeaderEdit
                  saveId="saveQmsFrameworkChange"
                  revertId="revertQmsFrameworkChange"
                  handleClick_save={handleClick_saveHeader(
                    "qmsFrameworkChangeId",
                    qmsFrameworkChangeId
                  )}
                  handleClick_revert={handleClick_revertHeader("qmsFrameworkChangeId")}
                >
                  <select
                    className="form-control form-control-sm w-100"
                    style={{ width: "50%", minWidth: "100px" }}
                    name="editQmsFrameworkChangeId"
                    id="editQmsFrameworkChangeId"
                    value={qmsFrameworkChangeId}
                    onChange={(e) => setQmsFrameworkChangeId(+e.target.value)}
                  >
                    <option key={`qfcOptionKey_0`} value="0">
                      None
                    </option>
                    {(qmsFrameworkChanges || []).map((qfc, index) => (
                      <option
                        key={`qfcOptionKey_${index}`}
                        value={qfc.qmsFrameworkChangeId}
                      >
                        {qfc.label}
                      </option>
                    ))}
                  </select>
                </CmtHeaderEdit>
              ) : (
                <span className="issue-subject">{getQmsFrameworkChange()}</span>
              )}
            </li>
            {issue && !issue.parentIssueId && (
              <li className="list-group-item border-bottom py-0 pl-1">
                <span className="mr-2 font-weight-bold">Input Category:</span>
                {showEdit && !fieldsInEditMode.categoryIds && (
                  <CmtHeaderEditButton
                    buttonId="editInputCategory"
                    handleClick={() => {
                      setInputCategory(
                        (issue.categoryIds || []).length > 0 ? issue.categoryIds[0] : 0
                      );
                      setFieldsInEditMode({ ...fieldsInEditMode, categoryIds: true });
                    }}
                  />
                )}
                <br />
                {fieldsInEditMode.categoryIds ? (
                  <CmtHeaderEdit
                    saveId="saveInputCategory"
                    revertId="revertInputCategory"
                    handleClick_save={handleClick_saveHeader(
                      "categoryIds",
                      inputCategory ? [inputCategory] : []
                    )}
                    handleClick_revert={handleClick_revertHeader("categoryIds")}
                  >
                    <select
                      className="form-control form-control-sm w-100"
                      style={{ width: "50%", minWidth: "100px" }}
                      name="editInputCategory"
                      id="editInputCategory"
                      value={inputCategory || 0}
                      onChange={(e) => setInputCategory(+e.target.value)}
                    >
                      <option key={`inputCatOptionKey_0`} value="0">
                        None
                      </option>
                      {(cmtCategories || []).map((cc, index) => (
                        <option
                          key={`inputCatOptionKey_${index}`}
                          value={cc.cmtCategoryId}
                        >
                          {`${cc.label}: ${cc.description}`}
                        </option>
                      ))}
                    </select>
                  </CmtHeaderEdit>
                ) : (
                  <span className="issue-subject">{getInputCategories()}</span>
                )}
              </li>
            )}
            <li className="list-group-item border-bottom py-0 pl-1">
              <span className="mr-2 font-weight-bold">Linked Documents:</span>
              {showEdit && !fieldsInEditMode.linkedDocuments && (
                <CmtHeaderEditButton
                  buttonId="editLinkedDocuments"
                  handleClick={() =>
                    setModalState_editLinkedDocuments({
                      showModal: true,
                      data: {
                        linkedDocuments: issue.linkedDocuments || [],
                        otherLinkedDocuments: issue.otherLinkedDocuments,
                      },
                    })
                  }
                />
              )}
              <br />
              <span className="issue-subject">{getLinkedDocuments()}</span>
            </li>
            <li className="list-group-item py-0 pl-1">
              <span className="mr-2 font-weight-bold">Files from Initiator:</span>
              <br />
              <div className="d-flex flex-column w-100">
                {issue && issue.attachments
                  ? issue.attachments.length > 0
                    ? (issue.attachments || []).map((a) => (
                        <button
                          key={`fileFromInitiator_${a.issueAttachmentId}`}
                          id={`fileFromInitiator_${a.issueAttachmentId}`}
                          name="fileFromInitiator"
                          className="btn btn-link py-0 px-1 text-small text-left"
                          style={{ textOverflow: "ellipsis" }}
                          onClick={handleClick_downloadFileFromInitiator(
                            a.issueAttachmentId,
                            a.originalFilename
                          )}
                        >
                          {a.originalFilename}
                        </button>
                      ))
                    : "No attachments"
                  : fetching
                  ? "Loading..."
                  : "(N/A)"}
              </div>
            </li>
          </ul>
        </div>
      </div>
      {modalState_editLinkedDocuments.showModal && (
        <CmtModal_EditLinkedDocuments
          modalState={modalState_editLinkedDocuments}
          toggleModal={toggleModal_editLinkedDocuments}
          divisionId={divisionId}
          issueId={issueId}
        />
      )}
    </>
  );
}

export function CmtHeaderEditButton(props) {
  const { buttonId, handleClick } = props;
  return (
    <button
      id={buttonId}
      name={buttonId}
      className="btn btn-link btn-sm align-top py-0 px-1"
      onClick={handleClick}
    >
      <FontAwesomeIcon icon="edit" className="fa-fw mr-1" />
      Edit...
    </button>
  );
}

function CmtHeaderEdit(props) {
  const { saveId, revertId, handleClick_save, handleClick_revert } = props;

  return (
    <div className="card bg-lightgray w-100 mb-2">
      <div className="card-body d-flex flex-row p-1 mt-1">
        <div className="flex-grow-1 mr-1">{props.children}</div>
        <button
          id={saveId}
          name={saveId}
          className="btn btn-sm btn-primary p-1 mr-1"
          onClick={handleClick_save}
        >
          <FontAwesomeIcon icon="check" className="fa-fw" />
        </button>
        <button
          id={revertId}
          name={revertId}
          className="btn btn-sm btn-secondary p-1"
          onClick={handleClick_revert}
        >
          <FontAwesomeIcon icon="times" className="fa-fw" />
        </button>
      </div>
    </div>
  );
}
